<template>
  <div class="__HeadContents">
    <CTitle large>
      {{ name }}

      <template v-slot:subtitle> ID: {{ id }} </template>
    </CTitle>

    <div class="__HeadContents-ActionButtons">
      <!-- Alert button -->
      <CButton
        :icon="isBudgetActive ? 'o_notifications_active' : 'o_notifications'"
        :color="isBudgetActive ? 'warning' : 'standard'"
        flat
        round
        material
        :tooltip="$t('Alert setting')"
        @click="onClickAlertSetting"
        v-if="!loading && isBudgetAlert && this.groupBy !== 'cross_tag'"
      />

      <DropdownMenu :items="menuItems" autoClose @click="onClickActionMenu" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DropdownMenu from '@design-system/components/DropdownMenu.vue';

import CTitle from '@/components/uikit/CTitle';
import CButton from '@/components/uikit/CButton';

const messages = {
  en: {
    'Edit account': 'Edit Account',
    'Alert setting': 'Alert Settings',
    'Edit group': 'Edit group',
    'Delete group': 'Delete group',
    'Budget setting': 'Budget Settings',
    'Fees info': 'Adjustments',
    'Reload data': 'Reload Data',
  },
  ja: {
    'Edit account': 'アカウントの編集',
    'Alert setting': 'アラートの設定',
    'Edit group': 'グループの編集',
    'Delete group': 'グループを削除',
    'Budget setting': '予算の設定',
    'Fees info': 'その他の明細情報',
    'Reload data': 'データを再取得',
  },
};

export default {
  name: 'HeadContents',
  i18n: { messages },
  components: { DropdownMenu, CTitle, CButton },
  props: {
    loading: {
      type: Boolean,
    },
  },
  computed: {
    ...mapState('usageReports', {
      groupBy: state => state.groupBy,
    }),

    ...mapGetters('usageReports', {
      name: 'getName',
      id: 'getId',
      detail: 'getDetail',
    }),

    ...mapState({
      dataType: state => state.userState.dataType,
      vendor: state => state.vendor,
    }),

    ...mapGetters('rbac', ['isResourceAllowed']),
    ...mapGetters('user', ['isAccessGroup']),

    ...mapGetters('user', {
      costType: 'getCostType',
    }),

    menuItems() {
      const items = [];

      switch (this.groupBy) {
        case 'account':
          if (this.isAllowedEditAccount) {
            items.splice(0, 0, {
              key: 'edit-account',
              label: this.$t('Edit account'),
              icon: 'o_edit',
            });
          }

          if (this.isAllowedBudgetSetting) {
            items.splice(1, 0, {
              key: 'budget-setting',
              label: this.$t('Budget setting'),
              icon: 'o_request_quote',
            });
          }

          if (this.idAllowedFees) {
            items.splice(2, 0, {
              key: 'extra-info',
              label: this.$t('Fees info'),
              icon: 'format_list_bulleted',
            });
          }

          break;
        case 'acc_group':
          if (this.isAllowedEditGroup) {
            items.splice(0, 0, { key: 'edit-group', label: this.$t('Edit group'), icon: 'o_edit' });

            if (this.isAllowedBudgetSetting) {
              items.splice(1, 0, {
                key: 'budget-setting',
                label: this.$t('Budget setting'),
                icon: 'o_request_quote',
              });
            }

            items.splice(3, 0, {
              key: 'delete-group',
              label: this.$t('Delete group'),
              color: 'danger',
              icon: 'o_delete',
            });
          }
          break;
        case 'cross_tag':
          break;
        case 'azure_subscription':
          if (this.idAllowedFees) {
            items.splice(2, 0, {
              key: 'extra-info',
              label: this.$t('Fees info'),
              icon: 'format_list_bulleted',
            });
          }
          break;
      }

      items.push({ key: 'reload', label: this.$t('Reload data'), icon: 'sync' });

      return items;
    },

    isBudgetAlert() {
      return this.$display('usage_account_menu_budget');
    },

    idAllowedFees() {
      return (
        this.$display('usage_account_menu_fees_fee') ||
        this.$display('usage_account_menu_fees_credit') ||
        this.$display('usage_account_menu_fees_refund') ||
        this.$display('usage_account_menu_fees_other_fees')
      );
    },

    isAllowedEditAccount() {
      return (
        this.$display('usage_account_menu_account_edit') &&
        this.$can('ModifyAccountSettings') &&
        this.isResourceAllowed(['ModifyAccountSettings'], 'account_id', this.id)
      );
    },

    isAllowedBudgetSetting() {
      return this.vendor === 'aws' && this.$display('cost_forecast_tab');
    },

    isAllowedAlertSetting() {
      if (this.groupBy !== 'account') return false;
      return (
        this.$display('usage_account_menu_budget') &&
        this.$can('ModifyAccountSettings') &&
        this.isResourceAllowed(['ModifyAccountSettings'], 'account_id', this.id)
      );
    },

    isBudgetActive() {
      if ((this.groupBy !== 'cross_tag' && this.groupBy !== 'account') || this.vendor === 'azure')
        return false;

      if (!this.detail) return false;
      if (!this.dataType) return false;

      const notification = this.detail[this.costType.replace('cost', 'notification')];

      return notification.daily || notification.monthly || notification.previous_day;
    },

    isAllowedEditGroup() {
      return (
        this.$can('ModifyGroups') && this.isResourceAllowed(['ModifyGroups'], 'group_id', this.id)
      );
    },
  },
  methods: {
    onClickActionMenu(val) {
      this.$emit('click-action', val);
    },

    onClickAlertSetting() {
      this.$emit('click-action', 'alert-setting');
    },
  },
};
</script>

<style lang="scss" scoped>
.__HeadContents {
  display: flex;
  justify-content: space-between;
  word-break: break-all;
  margin-bottom: $space-xs;

  &-ActionButtons {
    display: flex;

    & > div {
      margin-right: $space-xs;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
